// =============================================================================
// Copyright   : 2020 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : logging sagas
// =============================================================================
// ---Third-party imports
import {select, delay, put, takeEvery, call, putResolve} from 'redux-saga/effects'

// ---Framework imports
import {has, get, mapItems, dict, flatten, rstrip, isNil} from 'ldash'
import log from 'uix/logging'
import {UNIQUE_ID} from 'uix/actions'

// ---Local imports
import {setLogStreamData, toggleLogStream, setLogStream} from './actions'
import {applyChannelFilter, channelMatcher, uixmsg} from './utils'

// ---Globals
let logStream = (url, buffer) => {
  const controller = new AbortController()
  const signal = controller.signal
  const decoder = new TextDecoder('utf-8')
  let opts = {signal, crossDomain: true, credentials: 'include'}

  let fetchPromise = fetch(url, opts).then(response => {
    const reader = response.body.getReader()
    const stream = new ReadableStream({
      start(controller) {
        // The following function handles each data chunk
        function push() {
          // "done" is a Boolean and value a "Uint8Array"
          reader
            .read()
            .then(({done, value}) => {
              // Is there no more data to read?
              if (done) {
                // Tell the browser that we have finished sending data
                controller.close()
                log.info('stream closed remotely')
                buffer.push(uixmsg('...disconnected [done]...'))
                return
              }

              // Get the data and send it to the browser via the controller
              controller.enqueue(value)
              buffer.push(decoder.decode(value))
              push()
            })
            .catch(e => {
              log.debug('stream reader down')
              console.error(e)
              buffer.push(uixmsg(`...disconnected [catch ${String(e)}]...`))
            })
        }
        push()
      },
    })
    return new Response(stream, {headers: {'Content-Type': 'text/plain'}})
  })

  return {
    fetchPromise,
    abort: () => {
      log.debug('abort fetch')
      controller.abort()
    },
  }
}

// we only process last 50 lines
// XXX: why? increased to 500
const BAD_BUFFER = 500

function* _startLogStream({channelFilter, channel, limit, node}) {
  let line_filter = channelFilter?.map ? channelFilter.map(e => channelMatcher(e)) : null

  let {channel: prevChannel, limit: prevLimit} = yield select(root => root.meta.logstream)
  channel = channel || prevChannel
  limit = limit || prevLimit
  let apiurl = yield select(root => root.config.apiurl)

  let url = `${apiurl}/log3?name=${channel}&limit=${limit}`
  if (node && node != 'host') {
    url += `&node=${node}`
  }

  let buffer = []
  let DELAY = 200 // collect buffer each N millisecs
  let {fetchPromise, abort} = logStream(url, buffer)

  let streamidx = UNIQUE_ID().uid
  log.debug(`start stream ${streamidx}`)
  yield putResolve(setLogStream({on: true, channel, limit, node, streamidx}))
  yield putResolve(setLogStreamData([uixmsg(`connect to ${url} ...`)]))

  let isActive = true
  while (isActive) {
    if (buffer.length > 0) {
      let lines = flatten(buffer.map(chunk => chunk.split('\n')))
      // HTTPStreamHandler will send a [keepalive] if no new messages for 30 secs to prevent proxy timeouts
      lines = lines.map(l => rstrip(l)).filter(l => l && l !== '[keepalive]')
      if (line_filter) {
        lines = applyChannelFilter(lines, line_filter)
      }
      //~ yield put(setLogStreamData(lines.slice(-BAD_BUFFER)))
      yield put(setLogStreamData(lines))
      // clear buffer
      buffer.length = 0
    }
    yield delay(DELAY)

    let curr_logstream = yield select(root => root.meta.logstream)
    isActive =
      curr_logstream.on &&
      curr_logstream.channel == channel &&
      curr_logstream.streamidx == streamidx
  }
  log.info(uixmsg(`stop watching stream ${streamidx}`))
  abort()
}

function* _toggleLogStream({payload}) {
  let {active: setActive} = payload
  let isActive = yield select(root => root.meta.logstream.on)
  let [doStart, doStop] = [!isActive, isActive]

  if (!isNil(setActive)) {
    doStart = setActive
    doStop = isActive && !setActive
  }
  if (doStop) {
    yield putResolve(setLogStream({on: false}))
  }
  if (doStart) {
    yield _startLogStream(payload)
  }
}

export const sagas = {
  toggleLogStream: takeEvery(toggleLogStream, _toggleLogStream),
  checkRestartLogStream: takeEvery(setLogStream, function*({payload}) {
    let isActive = yield select(root => root.meta.logstream.on)
    // XXX - twisted: if we receive a streamidx, we already come from _startLogStream
    if (isActive && !payload.streamidx && (payload.channel || payload.limit || payload.node)) {
      yield putResolve(setLogStreamData('FLUSH'))
      yield _startLogStream({channel: payload.channel, limit: payload.limit, node: payload.node})
    }
  }),
}
